import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { merge } from 'lodash-es';
import QueryString from 'qs';
import {
  TModify,
  TApiaAction,
  TApiaSystemMessageObj,
  TApiaLoad,
  TApiaLoadForm,
} from '../../types';
import { arrayOrArray, asyncGetJSObjfromXML, clog } from '../../utils/util';
import { handle, IModalConfig, THandleConfiguration } from './ApiaApiHandler';
import EventEmitter from '../../utils/util/eventEmitter';
import debugDispatcher from '../../utils/debugDispatcher';
import {
  dispatchNotifications,
  getNotificationMessageObj,
  notify,
  TNotificationMessage,
} from '../../components/common/notifications/util';
import { handleDeadSession } from '../../components/apia/main/TabsList/ContentRenderer';
import { TModal } from '@apia/components';
import { ApiaUtil } from '@apia/components';
import { ApiaApi as AA } from '@apia/api';

export interface IApiaApiConsoleConfig {
  colors?: TColors;
  debug?: boolean;
}

export type TSetModalProps = (
  configuration: THandleConfiguration,
  formDefinition?: TApiaLoad<TApiaLoadForm>,
) => TModal | null;

export interface IApiaApiRequestConfig<DataType> extends IApiaApiConsoleConfig {
  axiosConfig?: AxiosRequestConfig;
  modalConfiguration?: IModalConfig;
  handleLoad?: boolean;
  setModalProps?: TSetModalProps;
  /**
   * Algunas veces las funciones llamadas por una acción en el servidor
   * tienen el mismo nombre siendo en realidad funciones distintas. Para
   * corregir este problema es posible pasar un path relativo al directorio
   * apiaApi/methods
   */
  methodsPath?: string;
  notificationsCategory?: string;
  queryData?: string | Record<string, unknown>;
  stringifyOptions?: QueryString.IStringifyOptions;
  validateResponse?: (
    response: AxiosResponse<DataType | null>,
  ) => Promise<boolean | string> | boolean | string;
}

debugDispatcher.on(
  'parseXml',
  async ([text]) => {
    const result = await asyncGetJSObjfromXML<unknown>(text as string);
    console.info(result);
  },
  'Acepta un parámetro de tipo string y realiza un parseo como si fuera xml, convirtiéndolo a objeto javascript.',
);

type TColors = {
  exception?: string;
  alert?: string;
  message?: string;
};

const defaultConfig: IApiaApiRequestConfig<unknown> = {
  debug: false,
  colors: {
    exception: 'red',
    alert: 'yellow',
    message: 'lightgreen',
  },
  handleLoad: false,
};

const STORED_CONFIG = 'ApiaApiConfig';

let forcedConfig: Partial<IApiaApiRequestConfig<unknown>> = {};
const storedConfig = localStorage.getItem(STORED_CONFIG);
if (storedConfig) {
  forcedConfig = JSON.parse(storedConfig) as Partial<
    IApiaApiRequestConfig<unknown>
  >;
}

window.ApiaApi = {
  reset() {
    forcedConfig = {};
    localStorage.removeItem(STORED_CONFIG);
  },
  config(newConfig) {
    if (
      newConfig.notificationsCategory ||
      newConfig.axiosConfig ||
      newConfig.modalConfiguration ||
      newConfig.handleLoad ||
      newConfig.queryData ||
      newConfig.stringifyOptions ||
      newConfig.validateResponse
    ) {
      throw new Error('Operación ilegal');
    }

    const toStoreConfig = merge(forcedConfig, newConfig);
    localStorage.setItem(STORED_CONFIG, JSON.stringify(toStoreConfig));
    forcedConfig = toStoreConfig;
    console.log('Se guardó la configuración correctamente', {
      config: toStoreConfig,
    });
  },
  shout() {
    console.log({ config: getConfig({}) });
  },
  theme(theme) {
    if (theme.toLowerCase() === 'dark') {
      window.ApiaApi.config({
        colors: {
          exception: 'red',
          alert: 'yellow',
          message: 'lightgreen',
        },
      });
    } else if (theme.toLowerCase() === 'light') {
      window.ApiaApi.config({
        colors: {
          exception: 'red',
          alert: '#e0e006',
          message: 'darkgreen',
        },
      });
    } else {
      console.log(`Tema ${theme} desconocido, prueba 'light' o 'dark'.`);
      return;
    }

    console.log(`Tema ${theme} establecido correctamente.`);
  },
};

function getConfig<LoadType>(
  outerBehaveConfig?: IApiaApiRequestConfig<LoadType>,
) {
  return merge({}, defaultConfig, outerBehaveConfig, forcedConfig);
}

function makeUrl(
  url: string,
  queryData: IApiaApiRequestConfig<unknown>['queryData'],
  stringifyOptions: IApiaApiRequestConfig<unknown>['stringifyOptions'],
) {
  let finalUrl = url;
  const questionMarkIndex = finalUrl.indexOf('?');

  if (questionMarkIndex === -1) {
    finalUrl += '?';
  } else if (
    questionMarkIndex !== finalUrl.length - 1 &&
    finalUrl.charAt(finalUrl.length - 1) !== '&'
  ) {
    finalUrl += '&';
  }
  if (
    finalUrl.indexOf('?timestamp=') === -1 &&
    finalUrl.indexOf('&timestamp=') === -1
  ) {
    finalUrl += `timestamp=${Date.now()}&`;
  }
  let parsedUrl = `${finalUrl}${
    queryData ? QueryString.stringify(queryData, stringifyOptions) : ''
  }`;
  let timestamp = `timestamp=${Date.now()}`;
  if (
    parsedUrl.indexOf('?timestamp=') !== -1 ||
    parsedUrl.indexOf('&timestamp=') !== -1
  ) {
    timestamp = '';
  }
  if (parsedUrl.endsWith('&') || parsedUrl.endsWith('?')) {
    if (timestamp.length > 0) {
      parsedUrl += timestamp;
    } else {
      parsedUrl = parsedUrl.slice(0, parsedUrl.length - 1);
    }
  } else if (timestamp.length > 0) {
    parsedUrl += `&${timestamp}`;
  }
  return parsedUrl;
}

const getURLActionName = (url: string): string => {
  const actionIdx = url.match(/action=(\w+)/)?.[1];
  return actionIdx ?? 'noAction';
};

export const ApiaActions = new (class ApiaActions extends EventEmitter<{
  action: TModify<TApiaAction, { param: string[] }>;
}> {})();

function getColor(
  color: keyof TColors,
  colors: TColors = defaultConfig.colors ?? {
    exception: 'red',
    alert: 'yellow',
    message: 'green',
  },
) {
  return colors[color] as string;
}

const handleWrongResponse = (error: Error) => {
  if (error instanceof AxiosError && error.status === 403) {
    handleDeadSession();
    return;
  }

  let errorMessage: string;
  if (typeof error !== 'string') {
    if (error.message) {
      errorMessage = error.message;
    } else {
      errorMessage = error.toString();
    }
  } else {
    errorMessage = error;
  }
  ApiaUtil.instance.notifications.notify({
    type: 'danger',
    message: error.message,
  });
  console.log('%c ', 'font-size:10vh');
  console.log('%cError in ApiaApi', 'color:red;font-size:2em;font-weight:bold');
  clog(`red/${errorMessage}`, { error });
  console.log('%c ', 'font-size:10vh');
};

export function isJsonResponse(response: AxiosResponse<unknown>) {
  return (response.headers['content-type'] as string).match('application/json');
}
export function isXmlResponse(response: AxiosResponse<unknown>) {
  return (response.headers['content-type'] as string).match(
    /(?:application|text)?\/xml/,
  );
}
export function isHtmlResponse(response: AxiosResponse<unknown>) {
  return (response.headers['content-type'] as string).match(
    /(?:application|text)?\/html/,
  );
}

function handleActions(
  actions: TApiaSystemMessageObj['actions'],
  behavior: IApiaApiRequestConfig<unknown>,
) {
  if (actions) {
    if (getConfig().debug) {
      console.log(
        '%cHandled actions: ',
        `color: ${getConfig().colors?.message ?? 'green'}`,
        { actions },
      );
    }
    const actionsArray = arrayOrArray(actions.action);
    actionsArray.forEach((action) => {
      behavior.modalConfiguration?.onAction?.(action);
      ApiaActions.emit('action', {
        ...action,
        param: arrayOrArray(action.param),
      });
    });
  }
}
function handleOnClose({
  exceptions,
  onClose,
  sysExceptions,
  sysMessages,
}: TModify<TNotificationMessage, { onClose: string }>) {
  try {
    import(
      /* webpackChunkName: "api-[request]" */ `./onClose/${onClose}.ts`
    ).then(
      (func: { default: () => void }) => {
        if (exceptions || sysExceptions || sysMessages) {
          const notificationsObject = getNotificationMessageObj({
            exceptions,
            onClose,
            sysExceptions,
            sysMessages,
          });
          if (notificationsObject) {
            notificationsObject.forEach((notification) => {
              notify({
                ...notification,
                onClose: func.default,
              });
            });
          } else {
            func.default();
          }
        } else {
          func.default();
        }
      },
      (e) => {
        notify({
          message: `onClose action not found: ${String(e)}`,
          type: 'danger',
        });
      },
    );
  } catch (e) {
    parseMessages({ exceptions, sysExceptions, sysMessages });
    console.error('Error while handling onClose');
    console.error(e);
  }
}

export function parseMessages(response: Record<string, unknown>) {
  if (!response) {
    return;
  }
  const { exceptions, sysMessages, sysExceptions } = response;
  if (exceptions || sysExceptions || sysMessages) {
    try {
      dispatchNotifications({
        exceptions,
        sysExceptions,
        sysMessages,
      } as TNotificationMessage);
    } catch (e: unknown) {
      handleWrongResponse(new Error(e as string));
    }
  }
}

export const parseSuccessfulResponse = async <
  LoadType extends Record<string, unknown>,
>(
  response: AxiosResponse<string>,
  currentUrl: string,
  outerBehaveConfig: IApiaApiRequestConfig<LoadType> = defaultConfig,
) => {
  const behaveConfig = getConfig(outerBehaveConfig);

  let parsedObj: TApiaSystemMessageObj<LoadType> | void =
    null as unknown as TApiaSystemMessageObj<LoadType>;

  if (isJsonResponse(response)) {
    if (typeof response.data === 'string') {
      parsedObj = JSON.parse(
        response.data.trim(),
      ) as TApiaSystemMessageObj<LoadType> | void;
    } else if (typeof response.data === 'object' && response.data) {
      parsedObj = response.data;
    }
  } else if (isXmlResponse(response)) {
    parsedObj = await asyncGetJSObjfromXML<LoadType>(response.data).catch(
      (e: unknown) => {
        handleWrongResponse(new Error(e as string));
      },
    );
  } else if (isHtmlResponse(response)) {
    console.error(
      'El contenido devuelto es Html, no se esperaba esa respuesta',
    );
    return null;
  }

  if (behaveConfig.validateResponse) {
    const validateResult = await behaveConfig.validateResponse({
      ...response,
      data:
        typeof parsedObj === 'object' && parsedObj
          ? { ...parsedObj, ...(parsedObj.load ?? {}) }
          : parsedObj,
    });
    if (typeof validateResult === 'string') {
      throw new Error(`Validation error: ${validateResult}`);
    } else if (validateResult === false) {
      throw new Error('Error');
    }
  }

  if (parsedObj) {
    const {
      actions,
      onClose,
      exceptions,
      sysExceptions,
      sysMessages,
      load,
      ...rest
    } = parsedObj; // Session handling
    if (rest.code === '-1' && exceptions) {
      (
        (window.frameElement as HTMLIFrameElement)?.handleDeadSession ??
        handleDeadSession
      )();
      return null;
    }

    if (exceptions && behaveConfig.debug) {
      clog(
        `${getColor('exception', behaveConfig.colors)}/parseSuccessfulResponse`,
        {
          exceptions,
        },
      );
    }
    if (sysExceptions && behaveConfig.debug) {
      clog(
        `${getColor('exception', behaveConfig.colors)}/parseSuccessfulResponse`,
        {
          sysExceptions,
        },
      );
    }
    if (sysMessages && behaveConfig.debug) {
      clog(
        `${getColor('alert', behaveConfig.colors)}/parseSuccessfulResponse`,
        {
          sysMessages,
        },
      );
    }

    handleActions(actions, behaveConfig);

    if (behaveConfig.handleLoad && onClose) {
      handleOnClose({
        exceptions,
        onClose,
        sysExceptions,
        sysMessages,
      });
    } else {
      parseMessages({ exceptions, sysExceptions, sysMessages });
    }
    if (load) {
      if (behaveConfig.handleLoad) {
        clog(`${getColor('message', behaveConfig.colors)}/handleLoad`, {
          load,
        });
        if (
          handle(load, currentUrl, {
            methodsPath: outerBehaveConfig.methodsPath,
            setModalProps: outerBehaveConfig.setModalProps,
            modalConfiguration: {
              ...behaveConfig.modalConfiguration,
              onClose: () => {
                if (onClose) {
                  handleOnClose({
                    exceptions,
                    onClose,
                    sysExceptions,
                    sysMessages,
                  });
                }
                if (behaveConfig.modalConfiguration?.onClose) {
                  behaveConfig.modalConfiguration?.onClose();
                }
              },
            },
          }) === false
        ) {
          clog(
            `${getColor(
              'exception',
              behaveConfig.colors,
            )}/unhandledLoad -> There is no handler defined`,
            {
              load,
            },
          );
        }
      }
      return { ...load, sysMessages, exceptions, sysExceptions };
    }
    return { ...(rest as LoadType), sysMessages, exceptions, sysExceptions };
  }

  return null;
};

export type TApiaApiAxiosResponse<LoadType> = AxiosResponse<LoadType | null> & {
  hasError: boolean;
  hasMessages: boolean;
};

async function handleResponse<LoadType extends Record<string, unknown>>(
  result: AxiosResponse<string> | void,
  currentUrl: string,
  outerBehaveConfig: IApiaApiRequestConfig<LoadType> = defaultConfig,
): Promise<TApiaApiAxiosResponse<LoadType> | null> {
  const behaveConfig = getConfig(outerBehaveConfig);
  try {
    if (!result || result.data === undefined) {
      if (behaveConfig.debug)
        clog(
          `${getColor('alert', behaveConfig.colors)}/ApiaApi wrong response`,
        );
    } else {
      const parsedResponse = await parseSuccessfulResponse<LoadType>(
        result,
        currentUrl,
        behaveConfig,
      );

      const action = getURLActionName(currentUrl);
      if (behaveConfig.debug)
        clog(
          `${getColor('message', behaveConfig.colors)}/ <- ApiaApi.${
            result.config.method ?? ''
          } ${action} `,
          {
            data: parsedResponse,
          },
        );

      return {
        ...result,
        data: parsedResponse,
        hasError:
          !!parsedResponse?.exceptions || !!parsedResponse?.sysExceptions,
        hasMessages: !!parsedResponse?.sysMessages,
      };
    }
  } catch (e: unknown) {
    handleWrongResponse(new Error(e as string));
    return null;
  }

  return null;
}

/**
* IMPORTANT!! The ApiaApi is programmed under the slogan that
 * **should never throw an exception**. **Errors** coming from
 * connectivity or exceptions on the server **will be handled
 * automatically** by ApiaApi, using the notification system
 * of the application. It is possible to determine in what context they should
 * be thrown those exceptions (global, main, modal) using the
 * alertsCategory configuration properties **(see errorsHandling.md)**.
 *
 * @param url String, the url you want to call
 * @param config interface IApiaApiRequestConfig<DataType>;
 * 
 * @throws Nothing
 * 
 * @example 
 * 
 * ApiaApi.post<ResponseType>('url...', {
    axiosConfig?: AxiosRequestConfig;

    // Configuración de postData
    postData? unknown;
    postDataTreatement?: 'stringify' | undefined;

    // Configuración de queryString
    queryData?: string | Record<string, unknown>;
    stringifyOptions?: QueryString.IStringifyOptions;

    // Response validator,
    // If it returns true, everything continues correctly
    // If it returns false, the ApiaApi throws a standard error.
    // If it returns string, ApiaApi throws the error returned in the string.
    validateResponse?: (
      response: AxiosResponse<DataType | null>,
    ) => boolean | string;

    // Configuración de alertas
    alertsImportance?: TAlertImportance;

    // Configuración de ApiaApiHandler, falta documentar
    handleLoad?: boolean;
    eventsHandler?: IEventsHandler;

    // other configuration
    colors?: TColors;
    debug?: boolean;
  }
 * @returns The type of the return value depends on the type passed when calling the function. If there was an error it returns null.
 */
async function post<
  LoadType extends Record<string, unknown> = Record<string, unknown>,
  DataType = unknown,
>(
  url:
    | string
    | TModify<
        IApiaApiRequestConfig<LoadType>,
        { postData?: DataType; postDataTreatement?: 'stringify' }
      >,
  config?: TModify<
    IApiaApiRequestConfig<LoadType>,
    {
      postData?: DataType;
      postDataTreatement?: 'stringify';
    }
  >,
): Promise<TApiaApiAxiosResponse<
  (LoadType & TNotificationMessage) | null
> | null>;
async function post<
  LoadType extends Record<string, unknown> = Record<string, unknown>,
  DataType = unknown,
>(
  config: TModify<
    IApiaApiRequestConfig<LoadType>,
    {
      postData?: DataType;
      postDataTreatement?: 'stringify';
    }
  >,
): Promise<TApiaApiAxiosResponse<
  (LoadType & TNotificationMessage) | null
> | null>;
async function post<
  LoadType extends Record<string, unknown> = Record<string, unknown>,
  DataType = unknown,
  T = unknown,
>(
  par1:
    | string
    | TModify<
        IApiaApiRequestConfig<LoadType>,
        { postData?: DataType; postDataTreatement?: 'stringify' }
      >,
  par2?: TModify<
    IApiaApiRequestConfig<LoadType>,
    { postData?: DataType; postDataTreatement?: 'stringify' }
  >,
): Promise<TApiaApiAxiosResponse<
  (LoadType & TNotificationMessage) | null
> | null> {
  const actualUrl = typeof par1 !== 'string' ? makeApiaUrl() : par1;

  const configParameter = (
    typeof par1 === 'string' ? (par2 ?? defaultConfig) : par1
  ) as TModify<
    IApiaApiRequestConfig<LoadType>,
    { postData?: DataType; postDataTreatement?: 'stringify' }
  >;

  const behaveConfig = {
    ...getConfig(configParameter),
    postData:
      configParameter.postDataTreatement === 'stringify'
        ? QueryString.stringify(
            configParameter.postData,
            configParameter.stringifyOptions,
          )
        : configParameter.postData,
  };

  const parsedUrl = makeUrl(
    actualUrl,
    behaveConfig.queryData,
    behaveConfig.stringifyOptions,
  );

  if (behaveConfig.debug) {
    const queryString = actualUrl.split('&');
    const action = getURLActionName(actualUrl);

    clog(`${getColor('message', behaveConfig.colors)}/ApiaApi.post ${action}`, {
      url: parsedUrl,
      queryDataInURL: [...queryString],
      queryData: behaveConfig.queryData,
      formData: behaveConfig.postData,
      stringifyOptiopns: behaveConfig.stringifyOptions,
    });
  }

  const response = await axios
    .post<
      T,
      AxiosResponse<string>,
      DataType | string
    >(parsedUrl, behaveConfig.postData, behaveConfig.axiosConfig)
    .catch((e: unknown) => {
      handleWrongResponse(e instanceof AxiosError ? e : new Error(e as string));
    });

  if (response) {
    const result = handleResponse<LoadType>(response, actualUrl, behaveConfig);
    return result;
  }
  return null;
}

/**
 * IMPORTANTE!! El ApiaApi está programado bajo la consigna de que
 * **no debe tirar núnca una excepción**. **Los errores** provenientes
 * de la conectividad o de excepciones en el servidor **serán manejados
 * automáticamente** por el ApiaApi utilizando el sistema de notificaciones
 * de la aplicación. Es posible determinar en qué contexto deben
 * ser lanzadas esas excepciones (global, main, modal) utilizando las
 * propiedades de configuración alertsCategory **(ver errorsHandling.md)**.
 *
 * @param url String, el url al que se desea llamar
 * @param config interface IApiaApiRequestConfig<DataType>;
 * 
 * @throws Nothing
 * 
 * @example 
 * 
 * ApiaApi.get<TipoDeRespuesta>('url...', {
    axiosConfig?: AxiosRequestConfig;

    // Configuración de queryString
    queryData?: string | Record<string, unknown>;
    stringifyOptions?: QueryString.IStringifyOptions;

    // Validador de respuesta, 
    // Si devuelve true, todo sigue correctamente
    // Si devuelve false, el ApiaApi tira un error estándar.
    // Si devuelve string, el ApiaApi tira el error devuelto en el string.
    validateResponse?: (
      response: AxiosResponse<DataType | null>,
    ) => boolean | string;

    // Configuración de alertas
    alertsImportance?: TAlertImportance;

    // Configuración de ApiaApiHandler, falta documentar
    handleLoad?: boolean;
    eventsHandler?: IEventsHandler;

    // Otra configuración
    colors?: TColors;
    debug?: boolean;
  }
 * @returns El tipo del valor devuelto depende del tipo pasado al llamar a la función. Si hubo un error devuelve null
 */
async function get<
  LoadType extends Record<string, unknown> = Record<string, unknown>,
>(
  url: string | IApiaApiRequestConfig<LoadType>,
  config?: IApiaApiRequestConfig<LoadType>,
): Promise<TApiaApiAxiosResponse<
  (LoadType & TNotificationMessage) | null
> | null>;
async function get<
  LoadType extends Record<string, unknown> = Record<string, unknown>,
>(
  config: IApiaApiRequestConfig<LoadType>,
): Promise<TApiaApiAxiosResponse<
  (LoadType & TNotificationMessage) | null
> | null>;
async function get<
  LoadType extends Record<string, unknown> = Record<string, unknown>,
  T = unknown,
  D = unknown,
>(
  par1: string | IApiaApiRequestConfig<LoadType>,
  par2?: IApiaApiRequestConfig<LoadType>,
): Promise<TApiaApiAxiosResponse<
  (LoadType & TNotificationMessage) | null
> | null> {
  const actualUrl = typeof par1 !== 'string' ? makeApiaUrl() : par1;

  const behaveConfig = getConfig<LoadType>(
    typeof par1 === 'string' ? (par2 ?? defaultConfig) : par1,
  );

  const parsedUrl = makeUrl(
    actualUrl,
    behaveConfig.queryData,
    behaveConfig.stringifyOptions,
  );

  if (behaveConfig.debug)
    clog(`${getColor('message', behaveConfig.colors)}/ApiaApi.get`, {
      url: parsedUrl,
    });
  const response = await axios
    .get<T, AxiosResponse<string>, D>(parsedUrl, behaveConfig.axiosConfig)
    .catch((e: unknown) => {
      handleWrongResponse(e instanceof AxiosError ? e : new Error(e as string));
    });
  if (response) {
    const result = await handleResponse<LoadType>(
      response,
      actualUrl,
      behaveConfig,
    );
    return result;
  }
  return null;
}

const ApiaApi = {
  get,
  getConfig,
  post,
  postMultiPart: AA.postMultiPart,
};
/**
 * Creates an url based on the general requirements of Apia.
 * If the ajaxUrl property is not passed, it will use the
 * window.URL_REQUEST_AJAX. Its use is very simple, just pass the objects you
 * want to appear in the query string.
 *
 * @example makeApiaUrl({
 *   ajaxUrl: 'the.endpoint.you.want',
 *   queryString: 'an=existent&query=string',
 *   action: 'theAction',
 *   anotherProp: 15
 * })
 *
 * @returns the well formed url, in the example, the response url is:
 * /context/the.endpoint.you.want?an=existent&query=string&anotherProp=15&tabId=...&tokenId=...&action=theAction
 */
export function makeApiaUrl(props?: {
  action?: string;
  ajaxUrl?: string;
  queryString?: string;
  stringifyOptions?: QueryString.IStringifyOptions;
  tabId?: string | number;
  preventAsXmlParameter?: boolean;
  avoidTabId?: boolean;
  avoidTimestamp?: boolean;
  [key: string]: unknown;
}) {
  let actualQueryData: Record<string, unknown> = {};

  if (props) {
    const {
      ajaxUrl,
      queryString,
      stringifyOptions,
      shouldAvoidTabId,
      preventAsXmlParameter,
      avoidTabId,
      avoidTimestamp,
      tabId: outerTabId,
      ...rest
    } = props;
    actualQueryData = { ...rest };
  }

  const queryString = QueryString.stringify(
    actualQueryData,
    props?.stringifyOptions ?? {
      arrayFormat: 'repeat',
      encodeValuesOnly: false,
    },
  );

  let actualAjaxUrl = props?.ajaxUrl ?? window.URL_REQUEST_AJAX;
  if (actualAjaxUrl.indexOf('?') === actualAjaxUrl.length - 1) {
    actualAjaxUrl = actualAjaxUrl.slice(0, actualAjaxUrl.length - 1);
  }
  if (!actualAjaxUrl.startsWith('/')) {
    actualAjaxUrl = `/${actualAjaxUrl}`;
  }

  const match = window.TAB_ID_REQUEST.match(/tokenId=(\w+)/);
  const currentTokenId = (match ?? [])[1];

  let tabId = (
    props?.tabId
      ? `&tabId=${props.tabId}&tokenId=${currentTokenId}`
      : window.TAB_ID_REQUEST
  ).slice(1);
  if (props?.avoidTabId) {
    tabId = '';
  }

  let { CONTEXT } = window;
  if (CONTEXT && CONTEXT.endsWith('/')) {
    CONTEXT += CONTEXT.slice(0, CONTEXT.length - 1);
  }

  let timestamp = `timestamp=${Date.now()}&`;
  if (
    (props?.queryString && props.queryString.indexOf('timestamp=') !== -1) ||
    (queryString && queryString.indexOf('timestamp=') !== -1)
  ) {
    timestamp = '';
  }

  const contextWord = CONTEXT.replace(/^\/?(.+?)\/?$/, '$1');
  actualAjaxUrl = actualAjaxUrl.match(
    new RegExp(`^(?:(?:/?${contextWord})?/)?(.+)$`),
  )?.[1] as string;

  let returnUrl = `${contextWord ? '/' : ''}${contextWord}/${actualAjaxUrl}?${
    props?.avoidTimestamp ? '' : timestamp
  }${!props?.preventAsXmlParameter ? 'asXml=true&' : ''}${
    props?.queryString ? `${props.queryString}&` : ''
  }${queryString ? `${queryString}&` : ''}${tabId}`;

  if (returnUrl.endsWith('&')) {
    returnUrl = returnUrl.substring(0, returnUrl.length - 1);
  }
  return returnUrl;
}

window.makeApiaUrl = makeApiaUrl;

export default ApiaApi;
